const mediumDateTimeFormatter = new Intl.DateTimeFormat("en", {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
});

/**
 * Full date with time — use for timestamps where precision matters,
 * e.g. "submitted at" or "last updated" displays.
 *
 * @example formatMediumDateTime(new Date("2024-01-15T14:30:00Z")) // "January 15, 2024 at 2:30 PM"
 * @example formatMediumDateTime("2024-01-15T14:30:00Z")           // also accepts ISO strings
 */
export function formatMediumDateTime(date: Date | string) {
  return mediumDateTimeFormatter.format(coerceDate(date));
}

const shortDateFormatter = new Intl.DateTimeFormat("en", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

/**
 * Compact date without time — the default choice for displaying dates in tables,
 * lists, and cards where space is limited and time-of-day isn't relevant.
 *
 * @example formatShortDate(new Date("2024-01-15")) // "Jan 15, 2024"
 * @example formatShortDate("2024-01-15")           // also accepts ISO strings
 */
export function formatShortDate(date: Date | string) {
  return shortDateFormatter.format(coerceDate(date));
}

/**
 * Human-readable date range — use for filter summaries, report headers, or
 * anywhere you need to describe a time window. Gracefully handles open-ended ranges.
 *
 * @example formatShortDateRange({ from: new Date("2024-01-15"), to: new Date("2024-01-20") }) // "Jan 15, 2024 - Jan 20, 2024"
 * @example formatShortDateRange({ from: new Date("2024-01-15") }) // "Since Jan 15, 2024"
 * @example formatShortDateRange({ to: new Date("2024-01-20") })   // "Until Jan 20, 2024"
 * @example formatShortDateRange({})                                // "All time"
 */
export function formatShortDateRange({ from, to }: { from?: Date; to?: Date }) {
  if (from) {
    if (to) {
      return `${formatShortDate(from)} - ${formatShortDate(to)}`;
    }

    return `Since ${formatShortDate(from)}`;
  } else if (to) {
    return `Until ${formatShortDate(to)}`;
  }

  return "All time";
}

const shortDateTimeWithYearFormatter = new Intl.DateTimeFormat("en", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
});

/**
 * Compact date *with* time but without the year — use for recent or same-year
 * activity feeds where the year is obvious and screen space is tight.
 *
 * Forces a comma separator (instead of locale-dependent "at") to avoid
 * SSR/client hydration mismatches between Node.js and browser Intl output.
 *
 * @example formatShortDateTime(new Date("2024-01-15T14:30:00Z")) // "Jan 15, 2024 2:30 PM"
 */
export function formatShortDateTime(date: Date | string) {
  return replaceLast(
    shortDateTimeWithYearFormatter
      .format(coerceDate(date))
      .replace(" at ", ", "),
    ",",
    "",
  );
}

const shortTimeFormatter = new Intl.DateTimeFormat("en", {
  hour: "numeric",
  minute: "numeric",
});

/**
 * Time of day only — use in lists already grouped under a date heading, where
 * repeating the date on every row is noise.
 *
 * @example formatShortTime(new Date("2024-01-15T14:30:00Z")) // "2:30 PM"
 * @example formatShortTime("2024-01-15T14:30:00Z")           // also accepts ISO strings
 */
export function formatShortTime(date: Date | string) {
  return shortTimeFormatter.format(coerceDate(date));
}

const monthYearFormatter = new Intl.DateTimeFormat("en", {
  month: "short",
  year: "numeric",
});

/**
 * Short month and year — use for display headers, report titles, or anywhere
 * you need a concise date representation without the day.
 *
 * @example formatMonthYear(new Date("2024-01-15")) // "Jan 2024"
 * @example formatMonthYear("2024-01-15")           // also accepts ISO strings
 */
export function formatMonthYear(date: Date | string) {
  return monthYearFormatter.format(coerceDate(date));
}

const monthFormatter = new Intl.DateTimeFormat("en", {
  month: "long",
});

/**
 * Full month name from a 1-indexed month number — use when you have a month
 * integer (e.g. from a database or form) and need a display label.
 *
 * @param month 1-indexed month (1 = January, 12 = December)
 * @example formatMonth(1)  // "January"
 * @example formatMonth(12) // "December"
 */
export function formatMonth(month: number) {
  return monthFormatter.format(new Date(0, month - 1));
}

function coerceDate(date: Date | string) {
  if (typeof date === "string") {
    return new Date(date);
  }

  return date;
}

/**
 * Extract the date portion of a Date as a `YYYY-MM-DD` string — use for
 * form default values, URL params, or anywhere you need a date-only ISO string.
 *
 * Note: uses UTC date components (via `.toISOString()`), so a late-evening
 * local time may produce the *next* day's date.
 *
 * @example toISODateString(new Date("2024-01-15T14:30:00Z")) // "2024-01-15"
 * @example toISODateString(undefined)                         // ""
 */
export function toISODateString(date?: Date) {
  if (!date) {
    return "";
  }

  return date.toISOString().split("T")[0];
}

function replaceLast(str: string, search: string, replacement: string) {
  const lastIndex = str.lastIndexOf(search);
  if (lastIndex === -1) return str;
  return (
    str.slice(0, lastIndex) + replacement + str.slice(lastIndex + search.length)
  );
}
